import type { ApiRequestBody, ApiResponseBody, ApiSearchQuery } from '@/lib/api/api-type-helper';

// ---------------------------------------------------------------------------
// List API Types
// ---------------------------------------------------------------------------

export type GetCustomerListQuery = ApiSearchQuery<'/v2/product-central/customers', 'get'>;
export type GetCustomerListResponse = ApiResponseBody<'/v2/product-central/customers', 'get'>;
export type CustomerListItem = GetCustomerListResponse['items'][0];
export type CustomerUsedProduct = CustomerListItem['usedProducts'][number];
export type CustomerOrderBy = NonNullable<GetCustomerListQuery>['orderBy'];

// ---------------------------------------------------------------------------
// Count API Types
// ---------------------------------------------------------------------------

export type GetCustomerCountQuery = ApiSearchQuery<'/v2/product-central/customers/count', 'get'>;
export type GetCustomerCountResponse = ApiResponseBody<'/v2/product-central/customers/count', 'get'>;

// ---------------------------------------------------------------------------
// Detail API Types
// ---------------------------------------------------------------------------

export type GetCustomerDetailResponse = ApiResponseBody<'/v2/product-central/customers/{id}', 'get'>;
export type GetCustomerValuesResponse = ApiResponseBody<'/v2/product-central/customers/{id}/values', 'get'>;
export type CustomerValueItem = GetCustomerValuesResponse['items'][number];
export type GetCustomerIssuesResponse = ApiResponseBody<'/v2/product-central/customers/{id}/issues', 'get'>;
export type CustomerIssueItem = GetCustomerIssuesResponse['items'][number];
export type GetCustomerIncidentsResponse = ApiResponseBody<'/v2/product-central/customers/{id}/incidents', 'get'>;
export type CustomerIncidentItem = GetCustomerIncidentsResponse['items'][number];

// ---------------------------------------------------------------------------
// Create API Types (顧客新規作成)
// ---------------------------------------------------------------------------

/**
 * 顧客作成リクエストボディ。
 * 必須: businessPartnerId / status / phase。
 * 任意: tier / ownerPersonId / startDate / endDate / note。
 * code は BE が CUS-NNN 自動採番、organizationId も BE 側で解決するため送らない。
 */
export type CreateCustomerRequest = ApiRequestBody<'/v2/product-central/customers', 'post'>;
/** 顧客作成レスポンス（詳細と同形。id を含む）。 */
export type CreateCustomerResponse = ApiResponseBody<'/v2/product-central/customers', 'post'>;

// ---------------------------------------------------------------------------
// Update API Types (顧客部分更新 / プロフィール編集)
// ---------------------------------------------------------------------------

/**
 * 顧客更新リクエストボディ（全フィールド任意の部分更新）。
 * - status / phase: 指定時のみ更新（null 不可）。
 * - tier / ownerPersonId / startDate / endDate: `null` を送ると解除。
 * - note: 空文字 `''` で消去（null 不可。DB は `@default("")`）。
 * businessPartnerId / code は更新対象外（リクエストに含まれない）。
 */
export type UpdateCustomerRequest = ApiRequestBody<'/v2/product-central/customers/{id}', 'patch'>;
/** 顧客更新レスポンス（詳細と同形）。 */
export type UpdateCustomerResponse = ApiResponseBody<'/v2/product-central/customers/{id}', 'patch'>;

// ---------------------------------------------------------------------------
// Link / Unlink API Types (価値・不具合の紐付け追加 / 解除)
// ---------------------------------------------------------------------------

export type AddCustomerValuesRequest = ApiRequestBody<'/v2/product-central/customers/{id}/values', 'post'>;
export type AddCustomerValuesResponse = ApiResponseBody<'/v2/product-central/customers/{id}/values', 'post'>;
export type RemoveCustomerValueResponse = ApiResponseBody<'/v2/product-central/customers/{id}/values/{valueId}', 'delete'>;
export type AddCustomerIssuesRequest = ApiRequestBody<'/v2/product-central/customers/{id}/issues', 'post'>;
export type AddCustomerIssuesResponse = ApiResponseBody<'/v2/product-central/customers/{id}/issues', 'post'>;
export type RemoveCustomerIssueResponse = ApiResponseBody<'/v2/product-central/customers/{id}/issues/{issueId}', 'delete'>;
export type AddCustomerIncidentsRequest = ApiRequestBody<'/v2/product-central/customers/{id}/incidents', 'post'>;
export type AddCustomerIncidentsResponse = ApiResponseBody<'/v2/product-central/customers/{id}/incidents', 'post'>;
export type RemoveCustomerIncidentResponse = ApiResponseBody<'/v2/product-central/customers/{id}/incidents/{incidentId}', 'delete'>;
export type AddCustomerTargetNodesRequest = ApiRequestBody<'/v2/product-central/customers/{id}/target-nodes', 'post'>;
export type AddCustomerTargetNodesResponse = ApiResponseBody<'/v2/product-central/customers/{id}/target-nodes', 'post'>;
export type RemoveCustomerTargetNodeResponse = ApiResponseBody<'/v2/product-central/customers/{id}/target-nodes/{targetNodeId}', 'delete'>;

// ---------------------------------------------------------------------------
// Customer Enums (BE 由来 3 値 x 3 種)
// ---------------------------------------------------------------------------

export const CustomerStatus = {
  ACTIVE: 'ACTIVE',
  SUSPENDED: 'SUSPENDED',
  TERMINATED: 'TERMINATED'
} as const;
export type CustomerStatus = (typeof CustomerStatus)[keyof typeof CustomerStatus];
const CUSTOMER_STATUS_VALUES: ReadonlySet<string> = new Set(Object.values(CustomerStatus));
export function isCustomerStatus(value: string | null | undefined): value is CustomerStatus {
  return value != null && CUSTOMER_STATUS_VALUES.has(value);
}

/** 一覧画面のステータスフィルタ用。ドメイン値 + "ALL"。 */
export type CustomerStatusFilter = CustomerStatus | 'ALL';
export const CustomerTier = {
  ENTERPRISE: 'ENTERPRISE',
  MID: 'MID',
  SMB: 'SMB'
} as const;
export type CustomerTier = (typeof CustomerTier)[keyof typeof CustomerTier];
const CUSTOMER_TIER_VALUES: ReadonlySet<string> = new Set(Object.values(CustomerTier));
export function isCustomerTier(value: string | null | undefined): value is CustomerTier {
  return value != null && CUSTOMER_TIER_VALUES.has(value);
}
export const CustomerPhase = {
  PRE_ONBOARDING: 'PRE_ONBOARDING',
  ONBOARDING: 'ONBOARDING',
  IN_PRODUCTION: 'IN_PRODUCTION'
} as const;
export type CustomerPhase = (typeof CustomerPhase)[keyof typeof CustomerPhase];
const CUSTOMER_PHASE_VALUES: ReadonlySet<string> = new Set(Object.values(CustomerPhase));
export function isCustomerPhase(value: string | null | undefined): value is CustomerPhase {
  return value != null && CUSTOMER_PHASE_VALUES.has(value);
}